import Edit from "@mui/icons-material/Edit";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import BasicMap from "components/BasicMap";
import { SectionComplianceIssuesButton } from "components/SectionComplianceIssuesButton";
import { tabsOffset } from "components/TabLinks";
import { haversineDistanceMiles } from "encamp-shared/src/geo/distance";
import { useCanEditFacility } from "hooks/useAuthorization";
import { useState } from "react";
import { formatAgencyDueDate } from "routes/Staff/Agencies/agencyDueDate";
import { formatMonthDayForDisplay } from "util/dates";

import { CUPA_ANNIVERSARY_DATE_TOOLTIP } from "../CupaAnniversaryDate";
import { useFacility, useLocalAgencyIssues } from "../useFacility";
import { AgencyType, EditLocalAgenciesDialog } from "./EditLocalAgenciesDialog";

export const LOCAL_AGENCIES_SECTION_ID = "local-agencies";
export const LOCAL_AGENCIES_SECTION_ICON = "📍";

function buildAddressLines(
  street1?: string | null,
  street2?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: string | null,
  country?: string | null
) {
  const lines: string[] = [];
  const streetLine = [street1, street2].filter(Boolean).join(", ");
  if (streetLine) lines.push(streetLine);
  const cityStateZip = [[city, state].filter(Boolean).join(" "), zip].filter(Boolean).join(" ");
  if (cityStateZip) lines.push(cityStateZip);
  if (country) lines.push(country);
  return lines;
}

interface AgencyDisplayProps {
  label: string;
  name?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  dueDate?: string | null;
  dueDateIsAnniversary?: boolean | null;
  cupaAnniversaryDate?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  facilityLatitude?: number | null;
  facilityLongitude?: number | null;
}

function AgencyDisplay({
  label,
  name,
  street1,
  street2,
  city,
  state,
  zip,
  country,
  latitude,
  longitude,
  facilityLatitude,
  facilityLongitude,
  dueDate,
  dueDateIsAnniversary,
  cupaAnniversaryDate,
}: AgencyDisplayProps) {
  const isCA = state === "CA";
  const hasCoordinates =
    latitude != null && longitude != null && !Number.isNaN(latitude) && !Number.isNaN(longitude);

  const addressLines = buildAddressLines(street1, street2, city, state, zip, country);

  const hasFacilityCoordinates =
    facilityLatitude != null &&
    facilityLongitude != null &&
    !Number.isNaN(facilityLatitude) &&
    !Number.isNaN(facilityLongitude);

  const distanceMiles =
    hasCoordinates && hasFacilityCoordinates
      ? haversineDistanceMiles(
          { latitude: facilityLatitude, longitude: facilityLongitude },
          { latitude: latitude!, longitude: longitude! }
        )
      : null;

  return (
    <Box>
      <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap>
        {/* Map and distance */}
        <Box sx={{ flexShrink: 0 }}>
          <Box sx={{ width: "240px", height: "196px" }}>
            {hasCoordinates ? (
              <BasicMap latitude={latitude} longitude={longitude} static />
            ) : (
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No location available.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
          {distanceMiles != null && (
            <Typography variant="caption" color="text.secondary" mt={1} display="block">
              {distanceMiles.toFixed(1)} miles from the facility
            </Typography>
          )}
        </Box>

        {/* Agency info */}
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
              {label}
            </Typography>
            {name && <Typography variant="body1">{name}</Typography>}
            <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
              {label.startsWith("Primary")
                ? "Primary: this agency will be included in your report in the portal."
                : "Secondary: this agency will receive a copy of your Tier II report."}
            </Typography>
          </Box>
          <Box>
            {addressLines.map((line, i) => (
              <Typography key={i} variant="body1">
                {line}
              </Typography>
            ))}
            {hasCoordinates && (
              <Typography variant="caption" color="text.secondary">
                (Shown on map)
              </Typography>
            )}
          </Box>
          {isCA && formatAgencyDueDate({ dueDate, dueDateIsAnniversary }) && (
            <Box>
              <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                Due Date
              </Typography>
              <Typography variant="body1">
                {formatAgencyDueDate({ dueDate, dueDateIsAnniversary })}
              </Typography>
            </Box>
          )}

          {isCA && dueDateIsAnniversary && cupaAnniversaryDate && (
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                fontSize="0.75rem"
                display="flex"
                alignItems="center"
                gap={1}
              >
                CUPA Anniversary Date
                <Tooltip title={CUPA_ANNIVERSARY_DATE_TOOLTIP}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </Typography>
              <Typography variant="body1">
                {formatMonthDayForDisplay(cupaAnniversaryDate)}
              </Typography>
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

export const LocalAgenciesSection: React.FC = () => {
  const theme = useTheme();
  const { data } = useFacility();
  const facility = data?.facility;
  const [editAgencyType, setEditAgencyType] = useState<AgencyType | null>(null);
  const canEdit = useCanEditFacility();

  const lepc = facility?.lepc;
  const fd = facility?.fireDepartment;
  const supplementalLepcs = facility?.supplementalLepcs ?? [];
  const supplementalFds = facility?.supplementalFireDepartments ?? [];
  const isCA = facility?.state === "CA";
  const sectionIssues = useLocalAgencyIssues();

  return (
    <Box
      id={LOCAL_AGENCIES_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h6">
          {LOCAL_AGENCIES_SECTION_ICON} Local Agencies (Chemicals)
        </Typography>
        <SectionComplianceIssuesButton
          issues={sectionIssues}
          sectionHeader={`${LOCAL_AGENCIES_SECTION_ICON} Local Agencies`}
        />
      </Stack>

      {editAgencyType && (
        <EditLocalAgenciesDialog
          agencyType={editAgencyType}
          onClose={() => setEditAgencyType(null)}
        />
      )}

      <Stack spacing={4} pl={2}>
        {/* LEPC / CUPA section */}
        <Stack
          spacing={4}
          sx={{
            "& .edit-icon-lepc": { opacity: 0, transition: "opacity 200ms" },
            "&:hover .edit-icon-lepc": { opacity: 1 },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">
              {isCA
                ? "Certified Unified Program Agency (CUPA)"
                : "Local Emergency Planning Committee (LEPC)"}
            </Typography>
            {canEdit && (
              <Tooltip title="Edit Section">
                <IconButton
                  size="small"
                  className="edit-icon-lepc"
                  sx={{ color: "action.active" }}
                  onClick={() => setEditAgencyType("lepc")}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <AgencyDisplay
            label={isCA ? "Primary CUPA" : "Primary LEPC"}
            name={lepc?.name}
            street1={lepc?.mailingAddress}
            street2={lepc?.mailingAddressLine2}
            city={lepc?.city}
            state={lepc?.state}
            zip={lepc?.zip}
            country="United States"
            dueDate={isCA ? lepc?.cupa?.dueDate : undefined}
            dueDateIsAnniversary={isCA ? lepc?.cupa?.dueDateIsAnniversary : undefined}
            cupaAnniversaryDate={isCA ? facility?.cupaAnniversaryDate : undefined}
            latitude={lepc?.latitude}
            longitude={lepc?.longitude}
            facilityLatitude={facility?.latitude}
            facilityLongitude={facility?.longitude}
          />

          {supplementalLepcs.map((sl) => (
            <AgencyDisplay
              key={sl.id}
              label={isCA ? "Supplemental CUPA" : "Supplemental LEPC"}
              name={sl.lepc.name}
              street1={sl.lepc.mailingAddress}
              street2={sl.lepc.mailingAddressLine2}
              city={sl.lepc.city}
              state={sl.lepc.state}
              zip={sl.lepc.zip}
              country="United States"
              dueDate={isCA ? sl.lepc.cupa?.dueDate : undefined}
              dueDateIsAnniversary={isCA ? sl.lepc.cupa?.dueDateIsAnniversary : undefined}
              cupaAnniversaryDate={isCA ? facility?.cupaAnniversaryDate : undefined}
              latitude={sl.lepc.latitude}
              longitude={sl.lepc.longitude}
              facilityLatitude={facility?.latitude}
              facilityLongitude={facility?.longitude}
            />
          ))}
        </Stack>

        {/* Fire Department — not shown for CA */}
        {!isCA && (
          <Stack
            spacing={4}
            sx={{
              "& .edit-icon-fd": { opacity: 0, transition: "opacity 200ms" },
              "&:hover .edit-icon-fd": { opacity: 1 },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Fire Department</Typography>
              {canEdit && (
                <Tooltip title="Edit Section">
                  <IconButton
                    size="small"
                    className="edit-icon-fd"
                    sx={{ color: "action.active" }}
                    onClick={() => setEditAgencyType("fireDepartment")}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            <AgencyDisplay
              label="Primary Fire Department"
              name={fd?.name}
              street1={fd?.mailingAddress}
              street2={fd?.mailingAddressLine2}
              city={fd?.city}
              state={fd?.state}
              zip={fd?.zip}
              country="United States"
              latitude={fd?.latitude}
              longitude={fd?.longitude}
              facilityLatitude={facility?.latitude}
              facilityLongitude={facility?.longitude}
            />

            {supplementalFds.map((sfd) => (
              <AgencyDisplay
                key={sfd.id}
                label="Supplemental Fire Department"
                name={sfd.fireDepartment.name}
                street1={sfd.fireDepartment.mailingAddress}
                street2={sfd.fireDepartment.mailingAddressLine2}
                city={sfd.fireDepartment.city}
                state={sfd.fireDepartment.state}
                zip={sfd.fireDepartment.zip}
                country="United States"
                latitude={sfd.fireDepartment.latitude}
                longitude={sfd.fireDepartment.longitude}
                facilityLatitude={facility?.latitude}
                facilityLongitude={facility?.longitude}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
